import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["input"]

    connect() {
        this.files = []
    }

    /**
     * Handles file input changes and updates the file list and previews.
     */
    handleFiles(event) {
        try {
            // Add new files to existing files array
            const newFiles = Array.from(event.target.files)
            this.files = [...this.files, ...newFiles]

            const previewContainer = this.getOrCreatePreviewContainer()

            // Add new file previews
            newFiles.forEach(file => {
                previewContainer.appendChild(this.createFilePreview(file.name, file.size))
            })

            // Update input's FileList with all files
            this.updateInputFileList()
        } catch (error) {
            console.error("Error handling files:", error)
        }
    }

    /**
     * Removes a file by its filename and updates the file list and previews.
     */
    removeFile(filename) {
        // Remove file from the tracked files array
        const fileIndex = this.files.findIndex(file => file.name === filename)
        if (fileIndex > -1) {
            this.files.splice(fileIndex, 1)
            this.updateInputFileList()
        }

        // Remove the corresponding preview element
        const previewContainer = this.element.querySelector("[data-vehicle-images-target=preview]")
        if (!previewContainer) return

        const previewElement = [...previewContainer.querySelectorAll(".single-attachment-preview")]
            .find(element => element.querySelector("span").textContent === filename)

        previewElement?.remove()

        // Remove the preview container if no files remain
        if (this.files.length === 0) {
            previewContainer.remove()
        }
    }

    /**
     * Creates a preview container if it doesn't exist.
     * @returns {HTMLElement} The preview container element.
     */
    getOrCreatePreviewContainer() {
        let previewContainer = this.element.querySelector("[data-vehicle-images-target=preview]")

        if (!previewContainer) {
            previewContainer = document.createElement("div")
            previewContainer.setAttribute("data-vehicle-images-target", "preview")
            previewContainer.className = "mt-4"
            previewContainer.style.minHeight = "50px"
            this.element.appendChild(previewContainer)
        }

        return previewContainer
    }

    /**
     * Updates the input's FileList to reflect the current files array.
     */
    updateInputFileList() {
        const dt = new DataTransfer()
        this.files.forEach(file => dt.items.add(file))
        this.inputTarget.files = dt.files
    }

    /**
     * Creates a file preview element.
     * @param {string} filename - The name of the file.
     * @param {number} size - The size of the file in bytes.
     * @returns {HTMLElement} The preview element.
     */
    createFilePreview(filename, size) {
        const fileDiv = document.createElement("div")
        fileDiv.className = "single-attachment-preview"

        const nameSpan = document.createElement("span")
        nameSpan.className = "flex-grow text-gray-800 dark:text-gray-100"
        nameSpan.textContent = filename

        const sizeSpan = document.createElement("span")
        sizeSpan.className = "text-sm text-gray-400"
        sizeSpan.textContent = this.formatFileSize(size)

        const removeSpan = document.createElement("span")
        removeSpan.className =
            "text-xs px-2 py-1 rounded bg-red-600 hover:bg-red-700 cursor-pointer transition-colors duration-200 text-white"
        removeSpan.textContent = "Remove"
        removeSpan.addEventListener("click", () => this.removeFile(filename))

        fileDiv.appendChild(nameSpan)
        fileDiv.appendChild(sizeSpan)
        fileDiv.appendChild(removeSpan)

        return fileDiv
    }

    /**
     * Formats a file size in bytes into a human-readable string.
     * @param {number} bytes - The file size in bytes.
     * @returns {string} The formatted file size.
     */
    formatFileSize(bytes) {
        if (bytes === 0) return "0 Bytes"
        const k = 1024
        const sizes = ["Bytes", "KB", "MB", "GB"]
        const i = Math.floor(Math.log(bytes) / Math.log(k))
        return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`
    }
}
